import { Action, createReducer, on } from '@ngrx/store';
import { EntityAdapter, EntityState } from '@ngrx/entity/src/models';
import { createEntityAdapter } from '@ngrx/entity';

import * as camerasActions from '@live/actions/live-cameras.actions';

import ICamera = Microsoft.VideoIndexer.Live.Contracts.ICamera;
import ICameraUpdate = Microsoft.VideoIndexer.Live.Contracts.ICameraUpdate;
import INextPage = Microsoft.VideoIndexer.Contracts.INextPage;

export interface PinnedCamera extends ICamera {
  error?: boolean;
  thumbnailUrl?: string;
  thumbnailError?: boolean;
}

export interface IPinnedState extends EntityState<PinnedCamera> {
  loaded: boolean;
  isLoading: boolean;
  error: boolean;
  nextPage: INextPage;
}

function selectPinnedCameraId(camera: PinnedCamera): string {
  return camera.id;
}

export const pinnedAdapter: EntityAdapter<PinnedCamera> = createEntityAdapter<PinnedCamera>({
  selectId: selectPinnedCameraId
});

export const initialPinnedState: IPinnedState = pinnedAdapter.getInitialState({
  loaded: false,
  isLoading: false,
  error: false,
  nextPage: {
    pageSize: null,
    skip: 0,
    done: false,
    totalCount: null
  }
});

const livePinnedCamerasReducer = createReducer(
  initialPinnedState,
  on(camerasActions.manageCamerasClicked, state => {
    return {
      ...state,
      error: false,
      loaded: true,
      isLoading: false
    };
  }),
  on(camerasActions.listPinnedCameras, state => {
    return {
      ...state,
      isLoading: true,
      loaded: !!state.nextPage.done
    };
  }),
  on(camerasActions.listPinnedCamerasSucceeded, (state, { pinnedCameras, nextPage }) => {
    // We use set all instead of upsert because we want to always clear and use the latest fresh fetched cameras state, never keep old state.
    return pinnedAdapter.setAll(pinnedCameras, {
      ...state,
      isLoading: false,
      loaded: true,
      error: false,
      nextPage
    });
  }),
  on(camerasActions.listPinnedCamerasFailed, state => {
    return pinnedAdapter.removeAll({
      ...state,
      isLoading: false,
      loaded: true,
      error: true
    });
  }),
  on(camerasActions.clearPinnedCameras, state => {
    return pinnedAdapter.removeAll({
      ...state,
      loaded: false,
      isLoading: false,
      error: false,
      nextPage: initialPinnedState.nextPage
    });
  }),
  on(camerasActions.updateCameraBatchSuccess, (state, { updatedCameras }) => {
    const pinnedUpdates = updatedCameras.filter(camera => state.entities[camera.id]).map(({ id, ...changes }) => ({ id, changes }));

    if (pinnedUpdates.length === 0) {
      return state;
    }

    return pinnedAdapter.updateMany(pinnedUpdates, state);
  }),
  on(camerasActions.getCameraThumbnailSuccess, (state, { cameraId, thumbnailUrl }) => {
    if (!state.entities[cameraId]) {
      return state;
    }

    return pinnedAdapter.updateOne(
      {
        id: cameraId,
        changes: {
          thumbnailUrl,
          thumbnailError: false
        }
      },
      state
    );
  }),
  on(camerasActions.getCameraThumbnailFailed, (state, { cameraId }) => {
    // Only update if the camera is in our pinned collection
    if (!state.entities[cameraId]) {
      return state;
    }

    return pinnedAdapter.updateOne(
      {
        id: cameraId,
        changes: {
          thumbnailUrl: null,
          thumbnailError: true
        }
      },
      state
    );
  }),
  on(camerasActions.getCameraSucceeded, (state, { camera }) => {
    if (state.entities[camera.id]) {
      return pinnedAdapter.upsertOne(camera, state);
    }
    return state;
  }),
  // When pinning a camera, we'll add it to pinned state immediately
  // The actual pin operation happens through effects and API calls
  // When those complete, listPinnedCamerasSucceeded will update with full data
  on(camerasActions.pinCamera, (state: IPinnedState, action) => {
    // If camera is already in pinned state, no need to add it again
    if (state.entities[action?.camera?.id]) {
      return state;
    }

    // Add a temporary camera as we doing a optimistic update
    // The camera will be removed if the pin operation fails
    // The camera will be kept in the state if the pin operation succeeds
    const newPinnedCamera: PinnedCamera = {
      ...action.camera,
      id: action.camera.id
    };

    return pinnedAdapter.addOne(newPinnedCamera, {
      ...state,
      nextPage: {
        ...state.nextPage,
        totalCount: (state.nextPage.totalCount || 0) + 1
      }
    });
  }),
  // When unpinning a camera, we'll remove it from pinned state immediately
  // The actual unpin operation happens through effects and API calls
  on(camerasActions.unpinCamera, (state, { camera }) => {
    if (state.entities[camera.id]) {
      return pinnedAdapter.removeOne(camera.id, {
        ...state,
        nextPage: {
          ...state.nextPage,
          totalCount: Math.max((state.nextPage.totalCount || 0) - 1, 0)
        }
      });
    }

    // If the camera was not in the pinned state, we don't need to remove it
    // This is a no-op, but we need to return the state to avoid errors
    return state;
  })
);

export function reducer(state: IPinnedState | undefined, action: Action) {
  return livePinnedCamerasReducer(state, action);
}
